import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import InputField from '../../inputField';
import CustomCheckbox from '../../customCheckbox';
import ActionButton from '../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minMax(0, 346px) auto auto auto 60px',
    gridColumnGap: spacing(2),
    alignItems: 'center',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '346px 1fr 1fr 1fr 60px',
    gridColumnGap: 8,
  },
  checkboxLabel: {
    justifyContent: 'center',
  },
  hasError: {
    marginBottom: spacing(1),
  },
  field: {
    position: 'relative',
    marginBottom: spacing(6),
  },
  errorMessage: {
    position: 'absolute',
    bottom: -20,
  },
  checkbox: {
    marginBottom: spacing(6),
  },
  checkboxWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    display: 'flex',
  },
  checkboxRoot: {
    marginTop: spacing(1),
    marginLeft: 0,
    marginRight: 0,
  },
  deleteButton: {
    marginBottom: spacing(5),
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    display: 'flex',
  },
}));

const DocumentTypeField = ({
  id,
  value,
  error = null,
  translations,
  fieldTypes,
  onDelete,
  onChange,
  isDisabled,
}) => {
  const classes = useStyles();
  const [textField, mandatoryCheckbox, selfUploadCheckbox] = fieldTypes;

  const handleChangeText = useCallback(
    e => {
      onChange({ ...value, [textField.name]: e.target.value }, id);
    },
    [id, value, onChange, textField.name]
  );

  const handleChangeCheckbox = useCallback(
    (isChecked, name) => {
      onChange({ ...value, [name]: isChecked }, id);
    },
    [id, value, onChange]
  );

  return (
    <div>
      <div className={classes.header}>
        <FormHelperText>
          {translations.headers.documentTypeLabel}
        </FormHelperText>
        <FormHelperText
          classes={{
            root: classes.checkboxLabel,
          }}
        >
          {translations.headers.mandatoryLabel}
        </FormHelperText>
        <FormHelperText
          classes={{
            root: classes.checkboxLabel,
          }}
        >
          {translations.headers.selfUploadLabel}
        </FormHelperText>
        <FormHelperText
          classes={{
            root: classes.checkboxLabel,
          }}
        >
          {translations.headers.expiryDateLabel}
        </FormHelperText>
      </div>
      <div className={classes.root}>
        <div
          className={classNames(classes.field, { [classes.hasError]: error })}
        >
          <InputField
            customErrorClass={classes.errorMessage}
            id={textField.name}
            name={textField.name}
            type={textField.type}
            placeholder={translations[textField.translationKey].placeholder}
            value={value?.[textField.name] || ''}
            error={!!error?.[textField.name]}
            errorMessage={
              translations.validationMessages?.[error?.[textField.name]]
            }
            disabled={isDisabled}
            onChange={handleChangeText}
            fullWidth
          />
        </div>
        <div className={classes.checkboxWrapper}>
          <CustomCheckbox
            className={classes.checkbox}
            customRootClass={classes.checkboxRoot}
            isChecked={value?.[mandatoryCheckbox.name]}
            disabled={isDisabled}
            isControlled={mandatoryCheckbox.isControlled}
            onChange={isChecked =>
              handleChangeCheckbox(isChecked, mandatoryCheckbox.name)
            }
          />
        </div>
        <div className={classes.checkboxWrapper}>
          <CustomCheckbox
            className={classes.checkbox}
            customRootClass={classes.checkboxRoot}
            isChecked={value?.[selfUploadCheckbox.name]}
            disabled={isDisabled}
            isControlled={selfUploadCheckbox.isControlled}
            onChange={isChecked =>
              handleChangeCheckbox(isChecked, selfUploadCheckbox.name)
            }
          />
        </div>
        <div className={classes.checkboxWrapper}>
          <CustomCheckbox
            className={classes.checkbox}
            customRootClass={classes.checkboxRoot}
            isChecked
            disabled
            onChange={() => {}}
          />
        </div>
        <div className={classes.deleteButton}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.DELETE_FILL}
            isDisabled={isDisabled}
            onClickHandler={onDelete}
          />
        </div>
      </div>
    </div>
  );
};

DocumentTypeField.propTypes = {
  translations: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  value: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  fieldTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDelete: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DocumentTypeField;
